import { useState } from 'react';

import { useResetPasswordMutation } from '@features/auth/hooks';
import eyeClosedIcon from '@shared/assets/images/eye-closed_icon.svg';
import eyeOpenIcon from '@shared/assets/images/eye-open_icon.svg';
import styles from '@pages/login/components/ChangePassword/ChangePassword.module.scss';
import validatePassword from '@features/user/utils/validatePassword';

/**
 * ChangePassword
 * - 비밀번호 재설정 화면
 * - 입력값 검증 및 비밀번호 변경 API 호출 담당
 * - 성공/실패 메시지는 부모 컴포넌트(Alert)로 위임
 */
export default function ChangePassword({ email, resetToken, onSuccess, onError, onCancel }) {
  /** =======================
     * 입력 상태
     ======================= */
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');

  // 비밀번호 표시/숨김 상태
  const [showPassword, setShowPassword] = useState({
    new: false,
    confirm: false,
  });

  /** =======================
     * 비밀번호 변경 Mutation
     ======================= */
  const { mutate: resetPassword, isPending } = useResetPasswordMutation({
    onSuccess() {
      // 비밀번호 변경 성공 → 부모에서 Alert 처리
      onSuccess?.();
    },
    onError(error) {
      // 서버 / 네트워크 에러
      const message = error?.response?.data?.message ?? '비밀번호 변경에 실패했습니다.';
      onError?.(message);
    },
  });

  /** =======================
     * 확인 버튼 클릭
     ======================= */
  const handleSubmit = () => {
    // 1. 입력값 존재 여부
    if (!password || !passwordConfirm) {
      onError?.('비밀번호를 입력해주세요.');
      return;
    }

    // 2. 비밀번호 일치 여부
    if (password !== passwordConfirm) {
      onError?.('새 비밀번호와 확인 비밀번호가 일치하지 않습니다.');
      return;
    }

    // 3. 비밀번호 정책
    if (!validatePassword(password)) {
      onError?.('비밀번호는 영문, 숫자, 특수문자를 포함한 8자 이상이어야 합니다.');
      return;
    }

    // 4. 비밀번호 변경 요청
    resetPassword({
      email,
      resetToken,
      changePw: password,
    });
  };

  /**
   * 비밀번호 표시/숨김 토글 핸들러
   */
  const toggleShowPassword = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  /** =======================
     * UI
     ======================= */
  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {/* 비밀번호 정책 안내 */}
        <div className={styles.guide}>· 영문, 숫자, 특수문자 혼합 8자 이상</div>

        {/* 새 비밀번호 */}
        <div className={styles.formRow}>
          <label>새 비밀번호</label>
          <div className={styles.inputWrapper}>
            <input
              type={showPassword.new ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className={styles.eyeButton}
              onClick={() => toggleShowPassword('new')}
            >
              <img
                src={showPassword.new ? eyeOpenIcon : eyeClosedIcon}
                alt=""
                className={styles.eyeIcon}
              />
            </button>
          </div>
        </div>

        {/* 새 비밀번호 확인 */}
        <div className={styles.formRow}>
          <label>새 비밀번호 확인</label>
          <div className={styles.inputWrapper}>
            <input
              type={showPassword.confirm ? 'text' : 'password'}
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
            />
            <button
              type="button"
              className={styles.eyeButton}
              onClick={() => toggleShowPassword('confirm')}
            >
              <img
                src={showPassword.confirm ? eyeOpenIcon : eyeClosedIcon}
                alt=""
                className={styles.eyeIcon}
              />
            </button>
          </div>
        </div>
      </div>

      {/* 하단 버튼 영역 */}
      <div className={styles.buttonArea}>
        <button type="button" onClick={onCancel} className={styles.cancelBtn}>
          취소
        </button>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={isPending}
          className={styles.confirm}
        >
          {isPending ? '변경 중...' : '확인'}
        </button>
      </div>
    </div>
  );
}
